import { useState, useCallback, useEffect, useRef } from "react";
import { validateCode } from "../../utils/registration/registrationHelpers.ts";
import { RESEND_CODE_COUNTDOWN } from "../../utils/registration/registrationConstants.ts";
import { validateJobboardCode, resendJobboardCode } from "../../services/jobboardRegistrationService.ts";

interface UseJobboardCodeValidationParams {
  email: string;
  onSuccess: () => void;
  onChangeEmail: () => void;
}

export const useJobboardCodeValidation = ({
  email,
  onSuccess,
  onChangeEmail,
}: UseJobboardCodeValidationParams) => {
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [countDown, setCountDown] = useState(RESEND_CODE_COUNTDOWN);
  const [showResend, setShowResend] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const handleSubmitRef = useRef<() => Promise<void>>();

  useEffect(() => {
    if (countDown > 0) {
      const timer = setTimeout(() => setCountDown(countDown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setShowResend(true);
    }
  }, [countDown]);

  const handleSubmit = useCallback(async () => {
    if (!validateCode(code)) {
      setCodeError("El código debe tener 6 dígitos");
      return;
    }
    setCodeError("");
    setIsValidating(true);
    try {
      await validateJobboardCode(email, code);
      onSuccess();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Código inválido. Intenta nuevamente.";
      setCodeError(errorMessage);
    } finally {
      setIsValidating(false);
    }
  }, [code, email, onSuccess]);

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  useEffect(() => {
    if (code.length === 6 && validateCode(code)) {
      handleSubmitRef.current?.();
    }
  }, [code]);

  const handleResend = useCallback(async () => {
    setIsResending(true);
    setCodeError("");
    try {
      await resendJobboardCode(email);
      setCountDown(RESEND_CODE_COUNTDOWN);
      setShowResend(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Error al reenviar el código.";
      setCodeError(errorMessage);
    } finally {
      setIsResending(false);
    }
  }, [email]);

  const handleChangeEmail = useCallback(() => {
    setCode("");
    setCodeError("");
    onChangeEmail();
  }, [onChangeEmail]);

  const handleCodeChange = useCallback((index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    setCode((prev) => {
      const newCode = prev.substring(0, index) + value + prev.substring(index + 1);
      return newCode;
    });
    if (value) setCodeError("");
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);
    if (/^\d{6}$/.test(pastedData)) {
      setCode(pastedData);
      setCodeError("");
    }
  }, []);

  return {
    code,
    setCode,
    codeError,
    countDown,
    showResend,
    handleResend,
    handleChangeEmail,
    handleCodeChange,
    handlePaste,
    isLoading: isValidating || isResending,
  };
};
